import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import api from '../lib/api';
import { format } from 'date-fns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Loader2,
  Globe,
  Share2,
  RefreshCw,
  Database,
  MapPin,
  Layers,
  Monitor,
  Film,
  Target,
  Building2,
  Calendar,
  Settings2,
  ChevronDown,
} from 'lucide-react';
import { cn, formatNumber, formatCurrency } from '../lib/utils';

// All available columns configuration
const ALL_COLUMNS = [
  // Dimensions
  { id: 'name', label: 'Name', category: 'dimension', defaultVisible: true },
  { id: 'id', label: 'ID', category: 'dimension', defaultVisible: false },
  { id: 'advertiser_name', label: 'Advertiser', category: 'dimension', defaultVisible: false },
  { id: 'advertiser_id', label: 'Advertiser ID', category: 'dimension', defaultVisible: false },
  { id: 'advertiser_currency', label: 'Currency', category: 'dimension', defaultVisible: false },
  { id: 'campaign_name', label: 'Campaign', category: 'dimension', defaultVisible: false },
  { id: 'campaign_id', label: 'Campaign ID', category: 'dimension', defaultVisible: false },
  { id: 'insertion_order_name', label: 'Insertion Order', category: 'dimension', defaultVisible: false },
  { id: 'insertion_order_id', label: 'Insertion Order ID', category: 'dimension', defaultVisible: false },
  { id: 'line_item_name', label: 'Line Item', category: 'dimension', defaultVisible: false },
  { id: 'line_item_id', label: 'Line Item ID', category: 'dimension', defaultVisible: false },
  { id: 'creative_name', label: 'Creative', category: 'dimension', defaultVisible: false },
  { id: 'creative_id', label: 'Creative ID', category: 'dimension', defaultVisible: false },
  { id: 'exchange', label: 'Exchange/Source', category: 'dimension', defaultVisible: false },
  { id: 'app_url', label: 'App/URL (Domain)', category: 'dimension', defaultVisible: false },
  { id: 'country', label: 'Country', category: 'dimension', defaultVisible: false },
  { id: 'city', label: 'City', category: 'dimension', defaultVisible: false },
  
  // Performance Metrics
  { id: 'impressions', label: 'Impressions', category: 'performance', defaultVisible: true, align: 'right' },
  { id: 'clicks', label: 'Clicks', category: 'performance', defaultVisible: true, align: 'right' },
  { id: 'spend', label: 'Spend', category: 'performance', defaultVisible: true, align: 'right', format: 'currency' },
  { id: 'ctr', label: 'CTR %', category: 'performance', defaultVisible: true, align: 'right', format: 'percent' },
  { id: 'cpc', label: 'CPC', category: 'performance', defaultVisible: true, align: 'right', format: 'currency' },
  { id: 'cpm', label: 'CPM', category: 'performance', defaultVisible: true, align: 'right', format: 'currency' },
  { id: 'conversions', label: 'Conversions', category: 'performance', defaultVisible: true, align: 'right' },
  
  // Video Metrics
  { id: 'video_starts', label: 'Video Starts', category: 'video', defaultVisible: true, align: 'right' },
  { id: 'video_first_quartile', label: 'Video 25%', category: 'video', defaultVisible: false, align: 'right' },
  { id: 'video_midpoint', label: 'Video 50%', category: 'video', defaultVisible: false, align: 'right' },
  { id: 'video_third_quartile', label: 'Video 75%', category: 'video', defaultVisible: false, align: 'right' },
  { id: 'video_completions', label: 'Video Complete', category: 'video', defaultVisible: true, align: 'right' },
  { id: 'video_completion_rate', label: 'VCR %', category: 'video', defaultVisible: true, align: 'right', format: 'percent' },
];

const DV360ComprehensiveReport = ({ dateRange }) => {
  const [groupBy, setGroupBy] = useState('campaign');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [lastSyncInfo, setLastSyncInfo] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(() => 
    ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.id)
  );

  const groupByOptions = [
    { id: 'date', label: 'Date', icon: Calendar },
    { id: 'advertiser', label: 'Advertiser', icon: Building2 },
    { id: 'campaign', label: 'Campaign', icon: Target },
    { id: 'insertion_order', label: 'Insertion Order', icon: Layers },
    { id: 'line_item', label: 'Line Item', icon: Monitor },
    { id: 'creative', label: 'Creative', icon: Film },
    { id: 'exchange', label: 'Exchange/Source', icon: Share2 },
    { id: 'app_url', label: 'App/URL (Domain)', icon: Globe },
    { id: 'country', label: 'Country', icon: MapPin },
    { id: 'city', label: 'City', icon: Building2 },
  ];

  const syncData = async () => {
    if (!dateRange?.from || !dateRange?.to) {
      toast.error('Please select a date range first');
      return;
    }

    setSyncing(true);
    try {
      const startDate = format(dateRange.from, 'yyyy-MM-dd');
      const endDate = format(dateRange.to, 'yyyy-MM-dd');
      
      toast.info('Syncing DV360 data from Bid Manager API... This may take 2-3 minutes.');
      
      const response = await api.post(`/dv360/sync-comprehensive-report`, null, {
        params: { start_date: startDate, end_date: endDate },
        timeout: 300000
      });

      if (response.data.status === 'success') {
        toast.success(`Synced ${response.data.records_synced?.toLocaleString()} records`);
        setLastSyncInfo({
          records: response.data.records_synced,
          dateRange: response.data.date_range
        });
        fetchData();
      } else {
        toast.warning(response.data.message || 'Sync completed with warnings');
      }
    } catch (error) {
      console.error('Error syncing DV360 data:', error);
      toast.error(error.response?.data?.detail || 'Failed to sync DV360 data');
    } finally {
      setSyncing(false);
    }
  };

  const fetchData = async () => {
    if (!dateRange?.from || !dateRange?.to) {
      return;
    }

    setLoading(true);
    try {
      const startDate = format(dateRange.from, 'yyyy-MM-dd');
      const endDate = format(dateRange.to, 'yyyy-MM-dd');
      
      const response = await api.get(`/dv360/comprehensive-report`, {
        params: { 
          start_date: startDate, 
          end_date: endDate,
          group_by: groupBy,
          limit: 500
        }
      });

      if (response.data) {
        setData(response.data.data || []);
        setTotalRecords(response.data.total_records || 0);
      }
    } catch (error) {
      console.error('Error fetching DV360 report:', error);
      if (error.response?.status !== 404) {
        toast.error(error.response?.data?.detail || 'Failed to fetch report data');
      }
      setData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dateRange?.from && dateRange?.to) {
      fetchData();
    }
  }, [dateRange, groupBy]);

  const toggleColumn = (columnId) => {
    setVisibleColumns(prev => 
      prev.includes(columnId) 
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };

  const selectAllColumns = () => {
    setVisibleColumns(ALL_COLUMNS.map(c => c.id));
  };

  const selectDefaultColumns = () => {
    setVisibleColumns(ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.id));
  };

  const getVisibleColumnConfigs = () => {
    return ALL_COLUMNS.filter(c => visibleColumns.includes(c.id));
  };

  // Calculate totals for visible numeric columns
  const totals = data.reduce((acc, item) => {
    ALL_COLUMNS.forEach(col => {
      if (col.category !== 'dimension' && typeof item[col.id] === 'number') {
        acc[col.id] = (acc[col.id] || 0) + item[col.id];
      }
    });
    return acc;
  }, {});

  // Recalculate rate metrics for totals
  if (totals.impressions > 0) {
    totals.ctr = (totals.clicks / totals.impressions * 100);
    totals.cpm = (totals.spend / totals.impressions * 1000);
  }
  if (totals.clicks > 0) {
    totals.cpc = totals.spend / totals.clicks;
  }
  if (totals.video_starts > 0) {
    totals.video_completion_rate = (totals.video_completions / totals.video_starts * 100);
  }

  const formatCellValue = (value, column) => {
    if (value === null || value === undefined || value === '') return '-';
    if (column.format === 'currency') return formatCurrency(value, 'USD');
    if (column.format === 'percent') return `${(typeof value === 'number' ? value : 0).toFixed(2)}%`;
    if (typeof value === 'number') return formatNumber(value);
    return value;
  };

  const currentGroupOption = groupByOptions.find(g => g.id === groupBy);
  const visibleColumnConfigs = getVisibleColumnConfigs();

  return (
    <Card className="border-slate-200 mt-4" data-testid="dv360-comprehensive-report">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Database className="w-4 h-4" />
            DV360 Comprehensive Report
            <span className="text-sm font-normal text-slate-500">
              (15 Dimensions + 10 Metrics)
            </span>
          </CardTitle>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Group By Selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Group by:</span>
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger className="w-[180px]" data-testid="dv360-group-by-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {groupByOptions.map((opt) => {
                    const Icon = opt.icon;
                    return (
                      <SelectItem key={opt.id} value={opt.id}>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          {opt.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Column Selector */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="column-selector-btn">
                  <Settings2 className="w-4 h-4 mr-2" />
                  Columns ({visibleColumns.length})
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 max-h-[400px] overflow-auto" align="end">
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b pb-2">
                    <span className="font-medium text-sm">Select Columns</span>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={selectAllColumns}>All</Button>
                      <Button variant="ghost" size="sm" onClick={selectDefaultColumns}>Default</Button>
                    </div>
                  </div>
                  
                  {/* Dimensions */}
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Dimensions</p>
                    <div className="space-y-1">
                      {ALL_COLUMNS.filter(c => c.category === 'dimension').map(col => (
                        <label key={col.id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded">
                          <Checkbox
                            checked={visibleColumns.includes(col.id)}
                            onCheckedChange={() => toggleColumn(col.id)}
                          />
                          <span className="text-sm">{col.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* Performance Metrics */}
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Performance Metrics</p>
                    <div className="space-y-1">
                      {ALL_COLUMNS.filter(c => c.category === 'performance').map(col => (
                        <label key={col.id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded">
                          <Checkbox
                            checked={visibleColumns.includes(col.id)}
                            onCheckedChange={() => toggleColumn(col.id)}
                          />
                          <span className="text-sm">{col.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* Video Metrics */}
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Video Metrics</p>
                    <div className="space-y-1">
                      {ALL_COLUMNS.filter(c => c.category === 'video').map(col => (
                        <label key={col.id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded">
                          <Checkbox
                            checked={visibleColumns.includes(col.id)}
                            onCheckedChange={() => toggleColumn(col.id)}
                          />
                          <span className="text-sm">{col.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* Sync Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={syncData}
              disabled={syncing}
              data-testid="sync-dv360-comprehensive-btn"
            >
              {syncing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Sync from API
                </>
              )}
            </Button>
          </div>
        </div>
        {lastSyncInfo && (
          <p className="text-xs text-green-600 mt-2">
            Last sync: {lastSyncInfo.records?.toLocaleString()} records for {lastSyncInfo.dateRange?.start} to {lastSyncInfo.dateRange?.end}
          </p>
        )}
      </CardHeader>
      <CardContent>
        {/* Data Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-slate-600">Loading report data...</span>
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Database className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-medium mb-2">No data available</p>
            <p className="text-sm">Click "Sync from API" to fetch DV360 data for the selected date range</p>
            <p className="text-xs mt-2 text-slate-400">
              This will pull all 15 dimensions and 10 metrics from Google Bid Manager API
            </p>
          </div>
        ) : (
          <>
            <div className="text-sm text-slate-500 mb-2">
              Showing {data.length} of {totalRecords.toLocaleString()} records grouped by {currentGroupOption?.label}
            </div>
            <div className="overflow-auto max-h-[600px] border border-slate-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-slate-50">
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-3 font-semibold text-slate-700 w-10">#</th>
                    {visibleColumnConfigs.map(col => (
                      <th 
                        key={col.id}
                        className={cn(
                          "py-3 px-3 font-semibold text-slate-700 whitespace-nowrap",
                          col.align === 'right' ? 'text-right' : 'text-left',
                          col.category === 'dimension' ? 'min-w-[150px]' : 'min-w-[100px]'
                        )}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, index) => {
                    const spendPercent = totals.spend > 0 ? ((item.spend / totals.spend) * 100) : 0;
                    return (
                      <tr 
                        key={`${item.id}-${index}`}
                        className="border-b border-slate-100 hover:bg-slate-50"
                      >
                        <td className="py-2.5 px-3 text-slate-400">{index + 1}</td>
                        {visibleColumnConfigs.map(col => (
                          <td 
                            key={col.id}
                            className={cn(
                              "py-2.5 px-3",
                              col.align === 'right' ? 'text-right font-mono' : 'text-left',
                              col.id === 'spend' && 'font-medium'
                            )}
                          >
                            {col.id === 'name' ? (
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-900 truncate max-w-[250px]" title={item.name || item.id}>
                                  {item.name || item.id || 'Unknown'}
                                </span>
                                {spendPercent >= 10 && (
                                  <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                                    {spendPercent.toFixed(1)}%
                                  </span>
                                )}
                              </div>
                            ) : (
                              formatCellValue(item[col.id], col)
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
                {/* Totals Row */}
                <tfoot className="sticky bottom-0 bg-slate-100 font-semibold">
                  <tr className="border-t-2 border-slate-300">
                    <td className="py-3 px-3"></td>
                    {visibleColumnConfigs.map(col => (
                      <td 
                        key={col.id}
                        className={cn(
                          "py-3 px-3",
                          col.align === 'right' ? 'text-right font-mono' : 'text-left'
                        )}
                      >
                        {col.id === 'name' ? (
                          <span className="text-slate-700">Total ({data.length} items)</span>
                        ) : col.category !== 'dimension' ? (
                          formatCellValue(totals[col.id], col)
                        ) : (
                          '-'
                        )}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default DV360ComprehensiveReport;
